import { useCallback, useEffect, useMemo, useState } from 'react';
import { PlantCrossShippingModal } from './PlantCrossShippingModal';
import { translations } from 'app/locales/i18n';
import { useTranslation } from 'react-i18next';
import { NotificationEntityType } from 'types';
import { useNavigate } from 'react-router';
import { useRouteParams } from 'app/shared/hooks/useRouteParams';
import { Table } from 'app/shared/components/generic-ui/Table/Table';
import { INotificationEntityGroup, Routes } from 'types';
import {
  CellAlignment,
  CellSize,
  PaginationType,
} from 'app/shared/components/generic-ui/Table/Table.types';
import { IAction } from 'app/shared/components/generic-ui/ActionButton/ActionButton';
import { usePlantCrossShippingGroupApi } from 'app/shared/hooks/usePlantCrossShippingGroupApi';
import { Button } from 'app/shared/components/generic-ui/Button/Button';
import { CustomTypography } from 'app/shared/components/generic-ui/Typography/Typography';
import { useNotificationEntityApi } from 'app/shared/hooks';
import { useSearchParams } from 'app/pages/notificationSettings/hooks';
import { PlantCrossShippingItem } from './PlantCrossShippingItem';

const skipOnePage = 1;

export const PlantCrossShipping = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [groupInfo, setGroupInfo] = useState<
    INotificationEntityGroup | undefined
  >(undefined);
  const navigate = useNavigate();

  const {
    page,
    perPage,
    tab,
    organizationId,
  }: {
    page: string;
    perPage: string;
    orderType: string;
    organizationId: string;
    tab: string;
  } = useRouteParams();

  const queryParams = useMemo(() => {
    return {
      organizationId: organizationId,
      queryParameters: {
        take: perPage,
        skip: `${(+page - skipOnePage) * +perPage}`,
        sortBy: 'name',
        sortAsc: true,
      },
    };
  }, [organizationId, page, perPage]);

  const { searchQuery } = useSearchParams();

  const searchQueryParams = useMemo(() => {
    return {
      organizationId: organizationId,
      entityType: NotificationEntityType.PLANT,
      queryParameters: {
        sortBy: 'lastTicketDispatchDateTimeUtc',
        sortAsc: false,
        query: searchQuery || '',
        skip: 0,
      },
    };
  }, [organizationId, searchQuery]);

  const {
    plantCrossShippingGroups,
    fetchPlantCrossShippingGroups,
    isFetchingPlantCrossShippingGroups,
    deletePlantCrossShippingGroup,
    createPlantCrossShippingGroup,
    updatePlantCrossShippingGroup,
    addPlantToCrossShippingGroup,
    removePlantFromCrossShippingGroup,
  } = usePlantCrossShippingGroupApi(queryParams);

  const {
    isFetchingNotificationEntities,
    fetchNotificationEntities,
    notificationEntities,
  } = useNotificationEntityApi(searchQueryParams);

  useEffect(() => {
    if (isModalOpen) {
      fetchNotificationEntities();
    }
  }, [fetchNotificationEntities, isModalOpen, searchQueryParams]);

  useEffect(() => {
    if (!groupInfo && !isModalOpen) {
      fetchPlantCrossShippingGroups();
    }
  }, [fetchPlantCrossShippingGroups, groupInfo, isModalOpen, queryParams]);

  const columns = useMemo(() => {
    return [
      {
        id: 'notificationEntityGroupName',
        label: t(translations.plantCrossShipping.groupName),
        alignment: CellAlignment.LEFT,
        size: CellSize.MEDIUM,
        sortable: false,
      },
      {
        id: 'notificationEntities',
        label: t(translations.plantCrossShipping.plants),
        alignment: CellAlignment.RIGHT,
        size: CellSize.MEDIUM,
        sortable: false,
      },
    ];
  }, [t]);

  const actions: IAction[] = useMemo(() => {
    return [
      {
        label: t(translations.plantCrossShipping.edit),
        onClick: (data: INotificationEntityGroup) => {
          setGroupInfo(data);
          setIsModalOpen(true);
        },
      },
      {
        label: t(translations.plantCrossShipping.remove),
        onClick: async (data: INotificationEntityGroup) => {
          await deletePlantCrossShippingGroup({
            notificationEntityGroupId: data.notificationEntityGroupId,
          });
          fetchPlantCrossShippingGroups();
        },
      },
    ];
  }, [deletePlantCrossShippingGroup, fetchPlantCrossShippingGroups, t]);

  const createNotificationEntityGroupHandler = useCallback(
    async (ids: string[], name?: string) => {
      if (name) {
        if (!groupInfo) {
          await createPlantCrossShippingGroup({
            notificationEntityGroupName: name,
            internalIds: ids,
          }).then(() => {
            fetchPlantCrossShippingGroups().then(() => {
              setIsModalOpen(false);
            });
          });
        } else {
          if (name !== groupInfo?.notificationEntityGroupName) {
            await updatePlantCrossShippingGroup({
              notificationEntityGroupId: groupInfo?.notificationEntityGroupId,
              notificationEntityGroupName: name,
            });
          }
          const plantsToAdd = ids.filter(
            id =>
              !groupInfo?.notificationEntities?.some(
                el => el.internalId === id,
              ),
          );
          const plantsToRemove = groupInfo?.notificationEntities?.filter(
            el => !ids.includes(el.internalId),
          );

          if (plantsToAdd.length) {
            await addPlantToCrossShippingGroup({
              notificationEntityGroupId: groupInfo?.notificationEntityGroupId,
              internalIds: plantsToAdd,
            });
          }
          if (plantsToRemove?.length) {
            await removePlantFromCrossShippingGroup({
              notificationEntityGroupId: groupInfo?.notificationEntityGroupId,
              internalIds: plantsToRemove.map(el => el.internalId),
            });
          }
          fetchPlantCrossShippingGroups().then(() => {
            setIsModalOpen(false);
          });
        }
      }
    },
    [
      addPlantToCrossShippingGroup,
      createPlantCrossShippingGroup,
      fetchPlantCrossShippingGroups,
      groupInfo,
      removePlantFromCrossShippingGroup,
      updatePlantCrossShippingGroup,
    ],
  );

  const renderCell = useCallback((rowData: any, cellId: string) => {
    if (cellId === 'notificationEntities') {
      return (
        <PlantCrossShippingItem
          notificationEntities={rowData.notificationEntities}
        />
      );
    } else {
      return <div>{rowData[cellId] || ''}</div>;
    }
  }, []);

  const searchColumns = useMemo(() => {
    return [
      {
        id: 'externalId',
        label: t(translations.notificationSettingsPage.id),
        alignment: CellAlignment.LEFT,
        size: CellSize.MEDIUM,
        sortable: false,
      },
      {
        id: 'name',
        label: t(translations.notificationSettingsPage.name, {
          entity: 'Plant',
        }),
        alignment: CellAlignment.LEFT,
        size: CellSize.MEDIUM,
        sortable: false,
      },
    ];
  }, [t]);

  return (
    <div>
      <div
        style={{
          margin: '10px 0',
          alignSelf: 'flex-end',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button variant="primaryProfile" onClick={() => setIsModalOpen(true)}>
          <CustomTypography variant="subtitle2" bold color="mbsBlue">
            {t(translations.plantCrossShipping.createGroup)}
          </CustomTypography>
        </Button>
      </div>
      <Table
        withHover={true}
        withPerPageCount={true}
        withPagination={true}
        columns={columns}
        tableData={plantCrossShippingGroups}
        withCheckbox={false}
        isLoading={isFetchingPlantCrossShippingGroups}
        emptyTableText={`${t(
          translations.plantCrossShipping.noCrossShippingGroups,
        )}.`}
        baseURL={Routes.OrganizationDetails.replace(
          ':organizationId/:tab',
          `${organizationId}/${tab}`,
        )}
        paginationType={PaginationType.MAIN}
        rowsTotal={0}
        renderCell={renderCell}
        withPadding={false}
        actions={actions}
        alignment="baseline"
      />
      {isModalOpen && (
        <PlantCrossShippingModal
          tableRowId="internalId"
          isOpen={isModalOpen}
          closeHandler={() => {
            setIsModalOpen(false);
            setGroupInfo(undefined);
            navigate({
              pathname: location.pathname.replace(
                `/${page}/${perPage}`,
                `/1/${perPage}`,
              ),
            });
          }}
          groupName={groupInfo?.notificationEntityGroupName}
          initSelectedItems={groupInfo?.notificationEntities?.map(
            el => el.internalId,
          )}
          isLoading={isFetchingNotificationEntities}
          header={
            !groupInfo
              ? t(translations.plantCrossShipping.plantCrossShipping)
              : t(translations.plantCrossShipping.addToGroup)
          }
          createItem={createNotificationEntityGroupHandler}
          withRemoveButton={false}
          withSearch
          columns={searchColumns}
          plants={notificationEntities}
          emptyTableText={`${t(translations.plantCrossShipping.noData)}.`}
          withTableHeader
          withTextField
          buttonText={
            !groupInfo
              ? t(translations.plantCrossShipping.createGroup)
              : t(translations.plantCrossShipping.update)
          }
          textFieldHeader={t(translations.plantCrossShipping.groupName)}
          searchHeader={
            !groupInfo
              ? t(translations.plantCrossShipping.addToGroup)
              : t(translations.plantCrossShipping.update)
          }
        />
      )}
    </div>
  );
};
